import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAuthStore } from '../store/authStore';
import { investigationService } from '../services/api';
import { formatDateGraph } from '../services/dateUtils';
import '../styles/DataSheetView.css';

// Same colour language as the main graph legend (Graph.js) — kept independent
// here since this view renders a plain HTML table, not Cytoscape elements.
const NODE_ROW_COLOR = '#0b3d2e'; // green-tinted row background
const NODE_ACCENT    = '#34d399';
const TX_ROW_COLOR   = '#0b2540'; // blue-tinted row background
const TX_ACCENT      = '#38bdf8';

function fmtVal(v) {
  if (v === null || v === undefined) return '';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `${(n / 1000).toFixed(2)}K`;
  return n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

export default function DataSheetView() {
  const { id }    = useParams();
  const { token } = useAuthStore();

  const [investigation, setInvestigation] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    if (!token) { setError('Not authenticated'); setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);

      const [invRes, graphRes, viewStateRes] = await Promise.all([
        investigationService.get(id),
        investigationService.getGraph(id),
        investigationService.getGraphViewState(id),
      ]);

      const inv   = invRes.data;
      const graph = graphRes.data || { nodes: [], edges: [] };
      const vs    = viewStateRes.data || {};
      const hiddenN = new Set(vs.hidden_nodes || []);
      const hiddenE = new Set(vs.hidden_edges || []);

      // Visible-only: same filtering logic as FocusedGraphView — drop hidden
      // nodes, then drop edges that are either explicitly hidden or whose
      // endpoint node is hidden.
      const visibleNodes = (graph.nodes || []).filter(n => {
        const addr = n.id || n.address || '';
        return addr && !hiddenN.has(addr);
      });
      const visibleNodeIds = new Set(visibleNodes.map(n => n.id || n.address || ''));
      const visibleEdges = (graph.edges || []).filter((e, i) => {
        const eid = e.id || `e${i}`;
        if (hiddenE.has(eid)) return false;
        const src = e.from || e.source || '';
        const tgt = e.to   || e.target || '';
        return visibleNodeIds.has(src) && visibleNodeIds.has(tgt);
      });

      setInvestigation(inv);
      setNodes(visibleNodes);
      setEdges(visibleEdges);
    } catch (err) {
      console.error('DataSheetView load error:', err);
      setError('Failed to load investigation data.');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const handleExportExcel = useCallback(() => {
    const nodeRows = nodes.map(n => ({
      Type:          'NODE',
      Address:       n.id || n.address || '',
      Blockchain:    n.blockchain || '',
      'Risk Score':  n.risk_score ?? '',
      'TX Count':    n.tx_count ?? '',
      'In Degree':   n.in_degree ?? '',
      'Out Degree':  n.out_degree ?? '',
    }));

    const txRows = edges.map(e => ({
      Type:        'TX',
      'TX Hash':   e.tx_hash || e.id || '',
      From:        e.from || e.source || '',
      To:          e.to   || e.target || '',
      Value:       e.value ?? '',
      Token:       e.token_symbol || '',
      Timestamp:   e.timestamp || '',
      Status:      e.status || '',
    }));

    const wb = XLSX.utils.book_new();

    const wsNodes = XLSX.utils.json_to_sheet(nodeRows);
    colorSheet(wsNodes, nodeRows.length, NODE_ROW_COLOR);
    XLSX.utils.book_append_sheet(wb, wsNodes, 'Nodes');

    const wsTx = XLSX.utils.json_to_sheet(txRows);
    colorSheet(wsTx, txRows.length, TX_ROW_COLOR);
    XLSX.utils.book_append_sheet(wb, wsTx, 'Transactions');

    const safeName = (investigation?.title || `investigation-${id}`).replace(/[^a-z0-9_-]+/gi, '_');
    XLSX.writeFile(wb, `${safeName}-datasheet.xlsx`);
  }, [nodes, edges, investigation, id]);

  // Best-effort cell fill colour. The community build of SheetJS (xlsx) does not
  // always persist cell styles when writing .xlsx — if Excel opens the file
  // without colour, the data and the two-sheet (Nodes / Transactions) split are
  // still fully intact; colour is a bonus, not load-bearing.
  function colorSheet(ws, rowCount, hexColor) {
    if (!ws['!ref'] || rowCount === 0) return;
    const range = XLSX.utils.decode_range(ws['!ref']);
    const argb = `FF${hexColor.replace('#', '').toUpperCase()}`;
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) continue;
        ws[addr].s = {
          fill: { patternType: 'solid', fgColor: { rgb: argb }, bgColor: { rgb: argb } },
          font: { color: { rgb: R === 0 ? 'FFFFFFFF' : 'FFE5E7EB' }, bold: R === 0 },
        };
      }
    }
  }

  if (loading) return <div className="datasheet-loading">Loading data sheet…</div>;
  if (error)   return <div className="datasheet-error">⚠ {error}</div>;

  return (
    <div className="datasheet-view">
      <div className="datasheet-topbar">
        <div className="datasheet-topbar-title">
          <h1>{investigation?.title}</h1>
          <span className="datasheet-badge">📊 Data Sheet</span>
        </div>

        <div className="datasheet-stats">
          <div className="datasheet-stat" style={{ color: NODE_ACCENT }}>
            <strong>{nodes.length}</strong>
            <label>Nodes</label>
          </div>
          <div className="datasheet-stat" style={{ color: TX_ACCENT }}>
            <strong>{edges.length}</strong>
            <label>Transactions</label>
          </div>
        </div>

        <div className="datasheet-topbar-sep" />
        <button className="datasheet-btn datasheet-btn-export" onClick={handleExportExcel}>
          📥 Export to Excel
        </button>
      </div>

      <div className="datasheet-legend">
        <span className="datasheet-legend-item"><span className="datasheet-dot" style={{ background: NODE_ACCENT }} /> Node (address)</span>
        <span className="datasheet-legend-item"><span className="datasheet-dot" style={{ background: TX_ACCENT }} /> Transaction</span>
        <span className="datasheet-legend-note">Showing only currently visible items (hidden nodes/TX excluded)</span>
      </div>

      <div className="datasheet-section">
        <h2 style={{ color: NODE_ACCENT }}>Nodes ({nodes.length})</h2>
        <div className="datasheet-table-wrap">
          <table className="datasheet-table datasheet-table-nodes">
            <thead>
              <tr>
                <th>Address</th>
                <th>Blockchain</th>
                <th>Risk Score</th>
                <th>TX Count</th>
                <th>In</th>
                <th>Out</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((n, i) => {
                const addr = n.id || n.address || '';
                return (
                  <tr key={addr || i} className="datasheet-row-node">
                    <td className="datasheet-mono">{addr}</td>
                    <td>{n.blockchain || ''}</td>
                    <td>{n.risk_score !== undefined ? Number(n.risk_score).toFixed(2) : ''}</td>
                    <td>{n.tx_count ?? ''}</td>
                    <td>{n.in_degree ?? ''}</td>
                    <td>{n.out_degree ?? ''}</td>
                  </tr>
                );
              })}
              {nodes.length === 0 && (
                <tr><td colSpan={6} className="datasheet-empty">No visible nodes.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="datasheet-section">
        <h2 style={{ color: TX_ACCENT }}>Transactions ({edges.length})</h2>
        <div className="datasheet-table-wrap">
          <table className="datasheet-table datasheet-table-tx">
            <thead>
              <tr>
                <th>TX Hash</th>
                <th>From</th>
                <th>To</th>
                <th>Value</th>
                <th>Token</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {edges.map((e, i) => (
                <tr key={e.id || i} className="datasheet-row-tx">
                  <td className="datasheet-mono">{e.tx_hash || e.id || ''}</td>
                  <td className="datasheet-mono">{e.from || e.source || ''}</td>
                  <td className="datasheet-mono">{e.to || e.target || ''}</td>
                  <td>{fmtVal(e.value)}</td>
                  <td>{e.token_symbol || ''}</td>
                  <td>{e.timestamp ? formatDateGraph(e.timestamp) : ''}</td>
                </tr>
              ))}
              {edges.length === 0 && (
                <tr><td colSpan={6} className="datasheet-empty">No visible transactions.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
